import {useRef} from 'react';
import {getGrecaptchaToken} from '@Libraries/recaptcha-library';
import {handleAuthFormSubmit} from '@Libraries/login-signup-library';

interface SubmitAuthFormOnce {
  submitAuthFormOnce: (submit: VoidFunction, recaptchaSelector: string) => void;
}

/**
 * Password managers that sign in automatically (1Password's auto-submit) press Enter in the field and click the
 * submit button, so one fill can submit a form twice. The second request then trips the server's per-email login
 * lock and shows an error even though the first one signed the user in.
 *
 * A submission is in flight from the moment the reCAPTCHA token is requested: the ref covers the token round trip,
 * and the login page's loading flag covers the request itself, staying set while a successful login navigates away.
 */
export const useSubmitAuthFormOnce = (): SubmitAuthFormOnce => {
  const isFetchingRecaptchaTokenRef = useRef(false);

  const isSubmissionInFlight = (): boolean => {
    return isFetchingRecaptchaTokenRef.current || window.PMW.redux.store.getState().loginPage.isLoading;
  };

  const submitAuthFormOnce = (submit: VoidFunction, recaptchaSelector: string): void => {
    if (!window.PMW.grecaptchaReady || isSubmissionInFlight()) {
      return;
    }

    isFetchingRecaptchaTokenRef.current = true;
    // grecaptcha.execute() hands back Google's own thenable, which has no finally().
    void Promise.resolve(getGrecaptchaToken(handleAuthFormSubmit.bind(null, submit), recaptchaSelector)).finally(() => {
      isFetchingRecaptchaTokenRef.current = false;
    });
  };

  return {submitAuthFormOnce};
};
